import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popconfirm } from "antd";
import { AppDispatch, RootState } from "../../../../app/store";
import {
  clearSavedScenarios,
  deleteScenario,
  loadScenario,
  renameScenario,
  saveScenario,
  selectCurrentScenarioMetrics,
  selectSavedScenarios,
  selectStatingToday,
  selectVolumeBasisKey,
} from "../../pricingToolSlice";
import {
  MAX_SAVED_SCENARIOS,
  SavedScenario,
  savedAtLabel,
  scenarioCaption,
  scenarioEffectiveRate,
  scenarioInputs,
  suggestScenarioName,
  volumeBasisKey,
} from "../../savedScenarios";
import { eur, pctFmt, txnsFmt } from "../shared/formatters";
import InfoTooltip from "../shared/InfoTooltip";
import styles from "../../PricingTool.module.css";

// A figure beside its caption, in the order the net-take panel states them.
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className={styles.saved_metric_label}>{label}</div>
      <div className={styles.saved_metric_value}>{value}</div>
    </div>
  );
}

// How much better or worse the pricing on screen is than a saved one, read left
// to right from the saved scenario to the current figures — the direction the
// net-take panel already reads its own "from X ▲ Y" in.
function CompareLine({
  saved,
  currentNet,
  currentRate,
}: {
  saved: SavedScenario;
  currentNet: number;
  currentRate: number;
}) {
  const netDelta = currentNet - saved.metrics.monthlyNet;
  const rateDelta = currentRate - scenarioEffectiveRate(saved.metrics);
  const up = netDelta >= 0;
  return (
    <div className={styles.saved_compare}>
      <span className={styles.saved_compare_label}>→ current</span>
      <span className={styles.saved_compare_value}>{eur(currentNet)} / mo</span>
      <span className={up ? styles.saved_delta_up : styles.saved_delta_down}>
        {up ? "▲" : "▼"} {eur(Math.abs(netDelta))} ·{" "}
        {pctFmt(Math.abs(rateDelta), 3)}
      </span>
    </div>
  );
}

export default function SavedScenarios() {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((s: RootState) => s.pricingTool);
  const scenarios = useSelector(selectSavedScenarios);
  const metrics = useSelector(selectCurrentScenarioMetrics);
  const currentBasis = useSelector(selectVolumeBasisKey);
  const statingToday = useSelector(selectStatingToday);

  const inputs = useMemo(() => scenarioInputs(state), [state]);
  const currentRate = scenarioEffectiveRate(metrics);

  // The name being typed: a new scenario when `id` is null, a rename otherwise.
  // Nothing is saved until it is confirmed, so an accidental click costs a key
  // press rather than a row.
  const [draft, setDraft] = useState<{
    id: string | null;
    name: string;
  } | null>(null);

  // Nothing has been modelled yet, so there is no pricing to keep.
  const savable = metrics.tpv > 0;

  const commitDraft = () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (name) {
      if (draft.id) dispatch(renameScenario({ id: draft.id, name }));
      else dispatch(saveScenario(name, metrics));
    }
    setDraft(null);
  };

  const nameEditor = (
    <div className={styles.saved_name_row}>
      <input
        autoFocus
        className={styles.saved_name_input}
        value={draft?.name ?? ""}
        maxLength={60}
        placeholder="Name this pricing…"
        onChange={(e) =>
          setDraft((d) => (d ? { ...d, name: e.target.value } : d))
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") commitDraft();
          if (e.key === "Escape") setDraft(null);
        }}
      />
      <button
        type="button"
        className={styles.saved_btn}
        onClick={commitDraft}
        disabled={!draft?.name.trim()}
      >
        {draft?.id ? "Rename" : "Save"}
      </button>
      <button
        type="button"
        className={styles.saved_btn_quiet}
        onClick={() => setDraft(null)}
      >
        Cancel
      </button>
    </div>
  );

  return (
    <div className={styles.saved_card}>
      <div className={styles.saved_header}>
        <div>
          <div className={styles.saved_title_row}>
            <span className={styles.saved_title}>Saved pricing</span>
            {scenarios.length > 0 && (
              <span className={styles.saved_count}>
                {scenarios.length} / {MAX_SAVED_SCENARIOS}
              </span>
            )}
          </div>
          <p className={styles.saved_sub}>
            Keep what this pricing came to, then try another template against
            the same volume and read the two side by side.
          </p>
        </div>
        <div className={styles.saved_actions}>
          <button
            type="button"
            className={styles.saved_btn}
            disabled={!savable}
            title={
              savable
                ? undefined
                : "No volume modelled yet — pick a business or an area first."
            }
            onClick={() =>
              setDraft({ id: null, name: suggestScenarioName(inputs) })
            }
          >
            Save current
          </button>
          {scenarios.length > 0 && (
            <Popconfirm
              title="Clear saved pricing"
              description={`Delete all ${scenarios.length} saved from this browser. This cannot be undone.`}
              okText="Clear all"
              cancelText="Keep"
              okButtonProps={{ danger: true }}
              onConfirm={() => dispatch(clearSavedScenarios())}
            >
              <button type="button" className={styles.saved_btn_ghost}>
                Clear all
              </button>
            </Popconfirm>
          )}
        </div>
      </div>

      {draft && !draft.id && nameEditor}

      {/* The live figures, as the row every saved one is compared against. */}
      <div className={`${styles.saved_row} ${styles.saved_row_live}`}>
        <div className={styles.saved_row_head}>
          <span className={styles.saved_row_name}>
            Current
            <span className={styles.saved_row_tag}>
              {statingToday ? "today" : "proposed"}
            </span>
          </span>
        </div>
        <div className={styles.saved_chips}>
          {scenarioCaption(inputs).map((c, i) => (
            <span key={i} className={styles.saved_chip}>
              {c}
            </span>
          ))}
        </div>
        <div className={styles.saved_metrics}>
          <Figure label="Effective rate" value={pctFmt(currentRate, 3)} />
          <Figure label="Net / mo" value={eur(metrics.monthlyNet)} />
          <Figure label="Net / yr" value={eur(metrics.annualNet)} />
        </div>
      </div>

      {scenarios.length === 0 ? (
        <p className={styles.saved_empty}>
          Nothing saved yet. Saved pricing lives in this browser only — it is
          not shared and it changes nothing live.
        </p>
      ) : (
        scenarios.map((s) => {
          const comparable = volumeBasisKey(s.inputs) === currentBasis;
          return (
            <div key={s.id} className={styles.saved_row}>
              <div className={styles.saved_row_head}>
                <span className={styles.saved_row_name}>{s.name}</span>
                <div className={styles.saved_row_actions}>
                  <span className={styles.saved_row_meta}>
                    {savedAtLabel(s.savedAt)}
                  </span>
                  <button
                    type="button"
                    className={styles.saved_icon_btn}
                    onClick={() => dispatch(loadScenario(s.id))}
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    className={styles.saved_icon_btn}
                    onClick={() => setDraft({ id: s.id, name: s.name })}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className={styles.saved_icon_btn}
                    aria-label={`Delete ${s.name}`}
                    onClick={() => dispatch(deleteScenario(s.id))}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {draft?.id === s.id && nameEditor}
              <div className={styles.saved_chips}>
                {scenarioCaption(s.inputs).map((c, i) => (
                  <span key={i} className={styles.saved_chip}>
                    {c}
                  </span>
                ))}
              </div>
              <div className={styles.saved_metrics}>
                <Figure
                  label="Effective rate"
                  value={pctFmt(scenarioEffectiveRate(s.metrics), 3)}
                />
                <Figure label="Net / mo" value={eur(s.metrics.monthlyNet)} />
                <Figure label="Net / yr" value={eur(s.metrics.annualNet)} />
              </div>
              {comparable ? (
                <CompareLine
                  saved={s}
                  currentNet={metrics.monthlyNet}
                  currentRate={currentRate}
                />
              ) : (
                <div className={styles.saved_compare}>
                  <span className={styles.saved_compare_label}>
                    Different volume basis
                  </span>
                  <InfoTooltip
                    header="Not compared"
                    body={`Saved against ${eur(s.metrics.tpv)} / mo over ${txnsFmt(s.metrics.txns)} transactions. The figures on screen are modelled on other volume, so the gap between the two would be mostly that — not the pricing.`}
                    hint="Load it to put the tool back on the volume it was saved against."
                  >
                    <span className={styles.saved_basis_help}>?</span>
                  </InfoTooltip>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
